import * as vd from "virtual-dom";
import { combineLatest as observableCombineLatest, of as observableOf, Subject } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, share, startWith, switchMap, tap, withLatestFrom, } from "rxjs/operators";
import { Component } from "../Component";
import { DirectionDOMRenderer } from "./DirectionDOMRenderer";
/**
 * @class DirectionComponent
 * @classdesc Component showing navigation arrows for steps and turns.
 */
export class DirectionComponent extends Component {
    constructor(name, container, navigator, directionDOMRenderer) {
        super(name, container, navigator);
        this._renderer = directionDOMRenderer
            ? directionDOMRenderer
            : new DirectionDOMRenderer(this.defaultConfiguration, {
                height: container.container.offsetHeight,
                width: container.container.offsetWidth,
            });
        this._hoveredKeySubject$ = new Subject();
        this._hoveredKey$ = this._hoveredKeySubject$.pipe(share());
    }
    /**
     * Get hovered key observable.
     *
     * @description An observable emitting the key of the node for the direction
     * arrow that is being hovered. When the mouse leaves a direction arrow null
     * is emitted.
     *
     * @returns {Observable<string>}
     */
    get hoveredKey$() {
        return this._hoveredKey$;
    }
    /**
     * Set highlight key.
     *
     * @description The arrow pointing towards the node corresponding to the
     * highlight key will be highlighted.
     *
     * @param {string} highlightKey Key of node to be highlighted if existing
     * among arrows.
     */
    setHighlightKey(highlightKey) {
        this.configure({ highlightKey: highlightKey });
    }
    /**
     * Set min width of container element.
     *
     * @description  Set min width of the non transformed container element holding
     * the navigation arrows. If the min width is larger than the max width the
     * min width value will be used.
     *
     * The container element is automatically resized when the resize
     * method on the Viewer class is called.
     *
     * @param {number} minWidth
     */
    setMinWidth(minWidth) {
        this.configure({ minWidth: minWidth });
    }
    /**
     * Set max width of container element.
     *
     * @description Set max width of the non transformed container element holding
     * the navigation arrows. If the min width is larger than the max width the
     * min width value will be used.
     *
     * The container element is automatically resized when the resize
     * method on the Viewer class is called.
     *
     * @param {number} minWidth
     */
    setMaxWidth(maxWidth) {
        this.configure({ maxWidth: maxWidth });
    }
    _activate() {
        this._configurationSubscription = this._configuration$.subscribe((configuration) => {
            this._renderer.setConfiguration(configuration);
        });
        this._resizeSubscription = this._container.renderService.size$.subscribe((size) => {
            this._renderer.resize(size);
        });
        this._nodeSubscription = this._navigator.stateService.currentNode$
            .pipe(tap((node) => {
            this._container.domRenderer.render$.next({ name: this._name, vnode: vd.h("div", {}, []) });
            this._renderer.setNode(node);
        }), withLatestFrom(this._configuration$), switchMap(([node, configuration]) => {
            return observableCombineLatest(node.spatialEdges$, configuration.distinguishSequence
                ? this._navigator.graphService.cacheSequenceNodes$(node.sequenceKey).pipe(catchError((error) => {
                    console.error(`Failed to cache sequence (${node.sequenceKey})`, error);
                    return observableOf(null);
                }))
                : observableOf(null));
        }))
            .subscribe(([edgeStatus, sequence]) => {
            this._renderer.setEdges(edgeStatus, sequence);
        });
        this._renderCameraSubscription = this._container.renderService.renderCamera$
            .pipe(tap((renderCamera) => {
            this._renderer.setRenderCamera(renderCamera);
        }), map(() => {
            return this._renderer;
        }), filter((renderer) => {
            return renderer.needsRender;
        }), map((renderer) => {
            return { name: this._name, vnode: renderer.render(this._navigator) };
        }))
            .subscribe(this._container.domRenderer.render$);
        this._currentNodeExternalSubscription = this._navigator.stateService.currentNodeExternal$
            .pipe(switchMap((node) => {
            return node.spatialEdges$;
        }), map(() => {
            return this._renderer;
        }), filter((renderer) => {
            return renderer.needsRender;
        }), map((renderer) => {
            return { name: this._name, vnode: renderer.render(this._navigator) };
        }))
            .subscribe(this._container.domRenderer.render$);
        this._hoveredKeySubscription = observableCombineLatest(this._container.domRenderer.element$, this._container.mouseService.mouseMove$.pipe(startWith(null)), this._container.mouseService.mouseUp$.pipe(startWith(null)))
            .pipe(map(([element]) => {
            const elements = (element.getElementsByClassName("mapillary-direction-perspective"));
            for (let i = 0; i < elements.length; i++) {
                const hovered = elements.item(i).querySelector(":hover");
                if (hovered != null && hovered.hasAttribute("data-key")) {
                    return hovered.getAttribute("data-key");
                }
            }
            return null;
        }), distinctUntilChanged())
            .subscribe(this._hoveredKeySubject$);
        this._emitHoveredKeySubscription = this._hoveredKey$.subscribe((key) => {
            this.fire(DirectionComponent.hoveredkeychanged, undefined, key);
        });
        // Initial render, set all the things for the first time
        this._renderer.setRenderCamera(this._container.scene.infrascene.traversalState.actualSceneCamera);
        this._container.domRenderer.render$.next({ name: this._name, vnode: this._renderer.render(this._navigator) });
    }
    _deactivate() {
        this._configurationSubscription.unsubscribe();
        this._emitHoveredKeySubscription.unsubscribe();
        this._hoveredKeySubscription.unsubscribe();
        this._nodeSubscription.unsubscribe();
        this._renderCameraSubscription.unsubscribe();
        this._resizeSubscription.unsubscribe();
        this._currentNodeExternalSubscription.unsubscribe();
    }
    _getDefaultConfiguration() {
        return {
            distinguishSequence: false,
            maxWidth: 360,
            minWidth: 260,
        };
    }
}
/** @inheritdoc */
DirectionComponent.componentName = "direction";
/**
 * Event fired when the hovered key changes.
 *
 * @description Emits the key of the node for the direction
 * arrow that is being hovered. When the mouse leaves a
 * direction arrow null is emitted.
 *
 * @event DirectionComponent#hoveredkeychanged
 * @type {string} The hovered key, null if no key is hovered.
 */
DirectionComponent.hoveredkeychanged = "hoveredkeychanged";
//# sourceMappingURL=DirectionComponent.js.map